import { Link } from '@tanstack/react-router';

import { SEO } from '@components/SEO';
import { Ripple } from '@components/ui/shadcn-io/ripple';
import { useLocale } from '@hooks/use-locale';
import usePageSeo from '@hooks/use-page-seo';
import { cn } from '@lib/utils';

export function NotFoundPage() {
  const { locale, localizeHref } = useLocale();
  const isTamil = locale === 'ta';
  const seo = usePageSeo({
    title: isTamil ? 'பக்கம் கிடைக்கவில்லை' : 'Thread not found',
    description: isTamil
      ? 'நீங்கள் தேடும் பட்டு கதை இன்னும் நெய்யப்பட்டுக் கொண்டிருக்கிறது. தொடக்கம் நோக்கி திரும்ப உதவுகிறோம்.'
      : 'The silk story you are seeking is still on our looms. Let us guide you back to where the magic begins.',
    canonicalPath: '/404',
    noIndex: true,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '404 - Thread not found',
      description: 'Custom 404 experience for Sree Silks',
    },
  });

  return (
    <section
      className="text-primary-foreground dark:text-foreground relative mx-auto flex w-full max-w-5xl flex-col items-center gap-8 overflow-hidden rounded-xl px-6 py-16 text-center"
      style={{
        background:
          'linear-gradient(135deg, var(--color-primary) 0%, color-mix(in srgb, var(--color-secondary) 40%, var(--color-primary) 60%) 55%, color-mix(in srgb, var(--color-background) 85%, transparent) 100%)',
      }}
    >
      <SEO {...seo} />
      <Ripple className="text-primary-foreground/30 dark:text-foreground/30" mainCircleSize={240} numCircles={7} />
      <div className="relative z-10 flex flex-col items-center gap-6">
        <span className="border-primary-foreground/40 bg-primary-foreground/10 dark:border-foreground/40 dark:bg-foreground/10 rounded-full border px-5 py-2 text-xs tracking-[0.5em] uppercase">
          404
        </span>
        <div className="space-y-3">
          <h1 className="text-3xl font-semibold sm:text-5xl">
            {isTamil ? 'இந்தப் பக்கத்தின் நூலை இழந்துவிட்டோம்' : 'We lost the thread of this page'}
          </h1>
          <p className="text-primary-foreground/80 dark:text-foreground/80 text-sm sm:text-base">
            {isTamil
              ? 'அது இன்னும் கைநெசவில் இருக்கலாம். எங்கள் பட்டு கலெக்ஷன்களை பாருங்கள் அல்லது முகப்பிற்கு திரும்புங்கள்.'
              : 'Perhaps it is still being handwoven. Explore our curated silks or return to the atelier entrance.'}
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to={localizeHref('/')}
            className={cn(
              'inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold tracking-[0.35em] uppercase transition',
              'bg-accent text-accent-foreground hover:bg-accent/90 focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2'
            )}
          >
            {isTamil ? 'முகப்பு' : 'Back Home'}
          </Link>
          <Link
            to={localizeHref('/collections')}
            className={cn(
              'inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold tracking-[0.35em] uppercase transition',
              'border-primary-foreground/50 text-primary-foreground hover:border-accent hover:text-accent dark:border-foreground/60 dark:text-foreground border bg-transparent'
            )}
          >
            {isTamil ? 'கலெக்ஷன்கள்' : 'View Collections'}
          </Link>
        </div>
        <div className="text-primary-foreground/70 dark:text-foreground/70 max-w-md space-y-2 text-xs">
          <p>
            {isTamil
              ? 'தனிப்பட்ட வழிகாட்டல் வேண்டுமா? தனிப்பயன் பரிசு யோசனைகளுக்கு எங்களை அணுகுங்கள்.'
              : 'Need a personal guide? Reach our concierge for bespoke gifting inspiration.'}
          </p>
          <Link
            to={localizeHref('/contact')}
            className="text-accent hover:text-accent/80 font-semibold underline-offset-4 hover:underline"
          >
            {isTamil ? 'எங்களுக்கு செய்தி அனுப்புங்கள்' : 'Send us a message'}
          </Link>
        </div>
      </div>
    </section>
  );
}
